import { useState, useEffect } from 'react';
import { Layout } from '@/components/common/Layout/Layout';
import { PageBody, PageHeader, PageShell } from '@/components/ui/page-shell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useStore } from '@/lib/store';
import { useToast } from '@/hooks/use-toast';
import { useClientsConfig } from '@/config/clients-config';
import api, { showErrorToast } from '@/lib/api';
import { ROUTES } from '@/constants/routes';

// Component imports
import { ClientsSearch } from '@/components/clients/ClientsSearch';
import { ClientsTable } from '@/components/clients/ClientsTable';
import { ClientForm } from '@/components/clients/ClientForm';

// Type and utility imports
import { Client } from '@/lib/store';
import { ClientFormData } from '@/components/clients/types';
import { exportClients } from '@/components/clients/export-utils';

export function Clients() {
  const { clients, fetchClients, fetchUsers } = useStore();
  const { toast } = useToast();
  const config = useClientsConfig();
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchClients(), fetchUsers()]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [fetchClients, fetchUsers]);

  // Form handlers
  const handleAddClient = () => {
    setSelectedClient(null);
    setIsFormOpen(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedClient(null);
  };

  const handleSubmitForm = async (formData: ClientFormData) => {
    setIsSubmitting(true);
    
    try {
      if (selectedClient) {
        // Update existing client
        await api.put(`${ROUTES.API.CLIENTS}${selectedClient.id}/`, formData);
        toast({
          title: 'Success',
          description: 'Client updated successfully',
        });
      } else {
        // Create new client
        await api.post(`${ROUTES.API.CLIENTS}`, formData);
        toast({
          title: 'Success',
          description: 'Client created successfully',
        });
      }
      
      // Refresh client list
      await fetchClients();
      handleCloseForm();
      
    } catch (error: any) {
      console.error('Client save error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 
          (selectedClient ? 'Failed to update client' : 'Failed to create client'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClient = async (clientId: number) => {
    try {
      await api.delete(`${ROUTES.API.CLIENTS}${clientId}/`);
      toast({
        title: 'Success',
        description: 'Client deleted successfully',
      });
      await fetchClients();
    } catch (error: any) {
      console.error('Client delete error:', error);
      showErrorToast(error, 'Error', 'Failed to delete client');
    }
  };

  // Export handlers
  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    try {
      await exportClients({ 
        format,
        includeContacts: true,
        includeVatInfo: config.showVatDetails,
      });
      
      toast({
        title: 'Success',
        description: `Clients exported as ${format.toUpperCase()} successfully`,
      });
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        title: 'Error',
        description: error.message || `Failed to export clients as ${format.toUpperCase()}`,
        variant: 'destructive',
      });
    }
  };

  return (
    <Layout>
      <PageShell>
        <PageHeader
          title="Clients"
          description="Manage your client relationships and information."
          actions={
            <Button onClick={handleAddClient}>
              <Plus className="mr-2 h-4 w-4" />
              Add client
            </Button>
          }
        />
        <PageBody>

        {/* Main Content Card */}
        <Card>
          <CardContent className="space-y-4 pt-6">
            {/* Search and Export Controls */}
            <ClientsSearch
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onExport={handleExport}
              enableExport={true}
            />

            {/* Table */}
            <ClientsTable
              clients={clients}
              loading={isLoading}
              onEdit={handleEditClient}
              onDelete={handleDeleteClient}
              searchQuery={searchQuery}
            />
          </CardContent>
        </Card>

        {/* Client Form Dialog */}
        <ClientForm
          client={selectedClient}
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          onSubmit={handleSubmitForm}
          isSubmitting={isSubmitting}
        />
        </PageBody>
      </PageShell>
    </Layout>
  );
}
